<script setup lang="ts">
import type { IconProps } from '/#/components/base/icon/icon'
import type { TagEmit, TagProps, TagVariants } from '/#/components/base/tag/tag'
import type { ComputedRef } from 'vue'

import { BODY } from '/@/constants/common'

const props = withDefaults(defineProps<TagProps>(), {
  label: '',
  icon: undefined,
  variant: 'solid',
  charLimit: undefined,
  tooltipText: undefined,
  color: 'slate',
  whiteIconBg: true,
})

const emit = defineEmits<TagEmit>()

const { createTooltip } = useEllipsisTooltip()

const appearance: ComputedRef<Array<string | boolean | TagVariants | undefined>> =
  computed(() => [
    props.color,
    props.variant,
    props.clickable && 'clickable',
    props.disabled && 'disabled',
    // Add slot-usage class if default slot is used
    props.slotUsage && 'slot-usage',
    // Sets min-width to "0". Because label's truncate class works with min-w-0.
    props.truncate && 'min-w-0',
  ])

/**
 * @var icon
 * @description Makes icon props easier to use. We don't need to size every time.
 * If only the icon name is defined, it will automatically be "size: small".
 */
const icon: ComputedRef<IconProps | undefined> = computed(() => {
  if (typeof props.icon === 'string') return { name: props.icon, size: 'sm' } as IconProps

  return props.icon as IconProps
})

/**
 * @var label
 * @description If a charLimit is defined and the label is longer than this limit, it truncates the label.
 * Otherwise, it returns the label itself.
 */
const label: ComputedRef<string> = computed(() => {
  const isLabelNeedEllipsis = props.charLimit && props.label.length > props.charLimit

  // If label is need ellipsis, crop the label and add dots after the label.
  if (isLabelNeedEllipsis) {
    const croppedLabel = props.label.substring(0, props.charLimit)

    return `${croppedLabel}...`
  }

  // Otherwise, return label's itself
  return props.label
})

/**
 * @var tooltip
 * @description Displays tooltip in case of charLimit exist or tooltip text given manually via prop.
 */
const tooltip: ComputedRef<string | undefined> = computed(() => {
  if (props.charLimit && props.label.length > props.charLimit) return props.label
  return props.tooltipText
})

const labelAppearance = computed(() => [
  props.truncate ? 'min-w-0 truncate' : 'min-w-max',
])
</script>

<template>
  <Tippy
    :key="label"
   
    :append-to="BODY"
    class="flex"
    :class="[truncate && 'min-w-0']"
  >
    <template v-if="tooltip" #content>
      <div class="tippy-box-content">{{ tooltip }}</div>
    </template>

    <div class="tag" :class="appearance">
      <span v-if="icon" :class="whiteIconBg && 'before-icon'">
        <Icon v-bind="icon" />
      </span>

      <slot :label="label">
        <span
         
          :class="[labelAppearance]"
          @mouseenter="createTooltip"
          @focusin="createTooltip"
        >
          {{ label }}
        </span>
      </slot>

      <Icon v-if="removable" name="close" @click.stop="emit('remove')" />
    </div>
  </Tippy>
</template>
